/**
 * DATETIME CONTRACT — single source of truth for date/time across the app.
 * See /app/memory/DATETIME_CONTRACT.md for the rules.
 *
 * • Zone: always SAST (Africa/Johannesburg, UTC+02:00).
 * • Date display: dd/mm/yyyy.
 * • Time display: 24h HH:mm.
 * • Storage: UTC ISO-8601 strings.
 * • Input: <input type="datetime-local"> whose value reads as the user's
 *   SAST wall clock; convert on submit via `fromSastLocalInput`.
 */

export const SAST_TZ = "Africa/Johannesburg";
export const SHIFT_START_SAST = "07:00";  // standard clock-in time
export const SHIFT_END_SAST = "16:30";    // standard clock-out time

const _safe = (v) => {
  if (v == null || v === "") return null;
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

/** "19/03/2026" — date-only display. */
export function fmtDate(iso) {
  const d = _safe(iso);
  if (!d) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: SAST_TZ,
    day: "2-digit", month: "2-digit", year: "numeric",
  }).format(d);
}

/** "07:32" — time-only display, 24h SAST. */
export function fmtTime(iso) {
  const d = _safe(iso);
  if (!d) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: SAST_TZ,
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(d);
}

/** "19/03/2026 07:32" — combined display, no zone suffix. */
export function fmtDateTime(iso) {
  const d = _safe(iso);
  if (!d) return "—";
  return `${fmtDate(d)} ${fmtTime(d)}`;
}

/** "19/03/2026 07:32 SAST" — combined display with explicit zone. */
export function fmtDateTimeWithTz(iso) {
  const d = _safe(iso);
  if (!d) return "—";
  return `${fmtDate(d)} ${fmtTime(d)} SAST`;
}

/** "Tue 19/03/2026 07:32" — long form with day-of-week. */
export function fmtDateTimeLong(iso) {
  const d = _safe(iso);
  if (!d) return "—";
  const dow = new Intl.DateTimeFormat("en-GB", {
    timeZone: SAST_TZ, weekday: "short",
  }).format(d);
  return `${dow} ${fmtDate(d)} ${fmtTime(d)}`;
}

/** "19/03" — table-cell short date (day/month only). */
export function fmtDateShort(iso) {
  const d = _safe(iso);
  if (!d) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: SAST_TZ, day: "2-digit", month: "2-digit",
  }).format(d);
}

/** "19/03 07:32" — table-cell short combined. */
export function fmtDateTimeShort(iso) {
  const d = _safe(iso);
  if (!d) return "—";
  return `${fmtDateShort(d)} ${fmtTime(d)}`;
}

/**
 * Convert a UTC ISO string → value usable inside a `<input type="datetime-local">`.
 * The returned string represents the SAST wall-clock equivalent so the input
 * SHOWS what the user sees. The input's `.value` then comes back through
 * `fromSastLocalInput()` on submit.
 *
 *   toSastLocalInput("2026-03-19T05:32:00Z")  →  "2026-03-19T07:32"
 */
export function toSastLocalInput(iso) {
  const d = _safe(iso);
  if (!d) return "";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: SAST_TZ,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d);
  const p = Object.fromEntries(
    parts.filter((x) => x.type !== "literal").map((x) => [x.type, x.value]),
  );
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/**
 * Convert a `<input type="datetime-local">` value (interpreted as a SAST
 * wall-clock) → UTC ISO string ready to POST.
 *
 *   fromSastLocalInput("2026-03-19T07:32")  →  "2026-03-19T05:32:00.000Z"
 */
export function fromSastLocalInput(localValue) {
  if (!localValue) return null;
  // Parse the literal date/time as if it were SAST.
  // SAST is UTC+02:00 with no DST → subtract 2 hours to get UTC.
  const [datePart, timePart] = localValue.split("T");
  if (!datePart) return null;
  const [y, m, d] = datePart.split("-").map(Number);
  const [hh = 0, mm = 0] = (timePart || "00:00").split(":").map(Number);
  // Build a UTC date directly from the components, then back off 2 hours.
  const ms = Date.UTC(y, m - 1, d, hh, mm, 0) - 2 * 60 * 60 * 1000;
  return new Date(ms).toISOString();
}

/** Convert a UTC ISO → minutes-since-midnight (in SAST). Used for early/late
 *  leaderboard ranking. */
export function minutesSinceMidnightSAST(iso) {
  const d = _safe(iso);
  if (!d) return null;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: SAST_TZ, hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d);
  const p = Object.fromEntries(parts.filter((x) => x.type !== "literal").map((x) => [x.type, x.value]));
  return Number(p.hour) * 60 + Number(p.minute);
}

/** "+12m" / "-8m" — minutes vs the standard SAST shift start (07:00). */
export function fmtMinutesVsShiftStart(iso) {
  const m = minutesSinceMidnightSAST(iso);
  if (m == null) return "—";
  const delta = m - (7 * 60);  // 07:00
  if (delta === 0) return "on time";
  if (delta < 0) return `${delta}m early`;
  return `+${delta}m late`;
}
